import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { candidatesApi, jobsApi, adminApi, formatApiError, extractApiErrorCode } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Archive, RotateCcw, Trash2, ArrowLeft, Loader2, Briefcase, Users, UserCog, Building2, AlertTriangle } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../../components/ui/tabs';
import { toast } from 'sonner';

function daysUntilPurge(deletedAt) {
    if (!deletedAt) return null;
    const deleted = new Date(deletedAt).getTime();
    const cutoff = deleted + 30 * 24 * 60 * 60 * 1000;
    const remaining = Math.max(0, Math.ceil((cutoff - Date.now()) / (24 * 60 * 60 * 1000)));
    return remaining;
}

const localeDate = (d) => d ? new Date(d).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'fr-FR') : '-';

export default function TrashPage() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user?.role === 'admin';
    const PAGE_SIZE = 10;

    const [candidates, setCandidates] = useState([]);
    const [candTotal, setCandTotal] = useState(0);
    const [candPage, setCandPage] = useState(1);
    const [candInput, setCandInput] = useState('');
    const [candSearch, setCandSearch] = useState('');
    const [canPurge, setCanPurge] = useState(false);

    const [jobs, setJobs] = useState([]);
    const [jobsTotal, setJobsTotal] = useState(0);
    const [jobsPage, setJobsPage] = useState(1);
    const [jobsInput, setJobsInput] = useState('');
    const [jobsSearch, setJobsSearch] = useState('');

    const [users, setUsers] = useState([]);
    const [usersTotal, setUsersTotal] = useState(0);
    const [usersPage, setUsersPage] = useState(1);
    const [usersInput, setUsersInput] = useState('');
    const [usersSearch, setUsersSearch] = useState('');

    const [orgs, setOrgs] = useState([]);
    const [orgsTotal, setOrgsTotal] = useState(0);
    const [orgsPage, setOrgsPage] = useState(1);
    const [orgsInput, setOrgsInput] = useState('');
    const [orgsSearch, setOrgsSearch] = useState('');

    const [loading, setLoading] = useState(true);
    const [purgeItem, setPurgeItem] = useState(null); // { type, id, name }
    const [orgPurgeTarget, setOrgPurgeTarget] = useState(null); // org permanent delete needs name confirmation
    const [orgConfirmName, setOrgConfirmName] = useState('');
    const [orgWorking, setOrgWorking] = useState(false);

    // Debounce search inputs -> applied search (reset to page 1 on change)
    useEffect(() => { const h = setTimeout(() => { setCandSearch(candInput); setCandPage(1); }, 350); return () => clearTimeout(h); }, [candInput]);
    useEffect(() => { const h = setTimeout(() => { setJobsSearch(jobsInput); setJobsPage(1); }, 350); return () => clearTimeout(h); }, [jobsInput]);
    useEffect(() => { const h = setTimeout(() => { setUsersSearch(usersInput); setUsersPage(1); }, 350); return () => clearTimeout(h); }, [usersInput]);
    useEffect(() => { const h = setTimeout(() => { setOrgsSearch(orgsInput); setOrgsPage(1); }, 350); return () => clearTimeout(h); }, [orgsInput]);

    const loadCandidates = useCallback(async () => {
        try {
            const res = await candidatesApi.getTrash({ skip: (candPage - 1) * PAGE_SIZE, limit: PAGE_SIZE, search: candSearch || undefined });
            const items = res.data.candidates || [];
            const t = res.data.total || 0;
            setCandidates(items);
            setCandTotal(t);
            setCanPurge(res.data.can_purge || false);
            if (items.length === 0 && t > 0 && candPage > 1) setCandPage(Math.ceil(t / PAGE_SIZE));
        } catch { /* ignore */ } finally { setLoading(false); }
    }, [candPage, candSearch]);

    const loadJobs = useCallback(async () => {
        try {
            const res = await jobsApi.getTrash({ skip: (jobsPage - 1) * PAGE_SIZE, limit: PAGE_SIZE, search: jobsSearch || undefined });
            const items = res.data.jobs || [];
            const t = res.data.total || 0;
            setJobs(items);
            setJobsTotal(t);
            if (items.length === 0 && t > 0 && jobsPage > 1) setJobsPage(Math.ceil(t / PAGE_SIZE));
        } catch { /* ignore */ }
    }, [jobsPage, jobsSearch]);

    const loadUsers = useCallback(async () => {
        if (!isAdmin) return;
        try {
            const res = await adminApi.getUsersTrash({ skip: (usersPage - 1) * PAGE_SIZE, limit: PAGE_SIZE, search: usersSearch || undefined });
            const items = res.data.users || [];
            const t = res.data.total || 0;
            setUsers(items);
            setUsersTotal(t);
            if (items.length === 0 && t > 0 && usersPage > 1) setUsersPage(Math.ceil(t / PAGE_SIZE));
        } catch { /* ignore */ }
    }, [isAdmin, usersPage, usersSearch]);

    const loadOrgs = useCallback(async () => {
        if (!isAdmin) return;
        try {
            const res = await adminApi.getOrganizationsTrash({ skip: (orgsPage - 1) * PAGE_SIZE, limit: PAGE_SIZE, search: orgsSearch || undefined });
            const items = res.data.organizations || [];
            const t = res.data.total || 0;
            setOrgs(items);
            setOrgsTotal(t);
            if (items.length === 0 && t > 0 && orgsPage > 1) setOrgsPage(Math.ceil(t / PAGE_SIZE));
        } catch { /* ignore */ }
    }, [isAdmin, orgsPage, orgsSearch]);

    useEffect(() => { loadCandidates(); }, [loadCandidates]);
    useEffect(() => { loadJobs(); }, [loadJobs]);
    useEffect(() => { loadUsers(); }, [loadUsers]);
    useEffect(() => { loadOrgs(); }, [loadOrgs]);

    const loadTrash = useCallback(() => {
        loadCandidates(); loadJobs(); loadUsers(); loadOrgs();
    }, [loadCandidates, loadJobs, loadUsers, loadOrgs]);

    const handleRestoreCandidate = async (id) => {
        try {
            await candidatesApi.restore(id);
            toast.success(t('trash.restore_success_candidate'));
            loadTrash();
        } catch (error) {
            toast.error(formatApiError(error));
        }
    };

    const handleRestoreJob = async (id) => {
        try {
            await jobsApi.restore(id);
            toast.success(t('trash.restore_success_job'));
            loadTrash();
        } catch (error) {
            toast.error(formatApiError(error));
        }
    };

    const handleRestoreUser = async (id) => {
        try {
            await adminApi.restoreUser(id);
            toast.success(t('trash.restore_success_user'));
            loadTrash();
        } catch (error) {
            toast.error(formatApiError(error));
        }
    };

    const handleRestoreOrg = async (id) => {
        try {
            await adminApi.restoreOrganization(id);
            toast.success(t('trash.restore_success_org'));
            loadTrash();
        } catch (error) {
            const code = extractApiErrorCode(error);
            if (code === 'organization_name_conflict') {
                toast.error(error.response?.data?.detail?.message || formatApiError(error));
            } else {
                toast.error(formatApiError(error));
            }
        }
    };

    const handlePurgeOrg = async () => {
        if (!orgPurgeTarget) return;
        setOrgWorking(true);
        try {
            await adminApi.permanentlyDeleteOrganization(orgPurgeTarget.id, orgConfirmName);
            toast.success(t('trash.purge_success_org'));
            setOrgPurgeTarget(null);
            setOrgConfirmName('');
            loadTrash();
        } catch (error) {
            toast.error(formatApiError(error));
        } finally {
            setOrgWorking(false);
        }
    };

    const handlePurge = async () => {
        if (!purgeItem) return;
        try {
            if (purgeItem.type === 'candidate') {
                await candidatesApi.purge(purgeItem.id);
                toast.success(t('trash.purge_success_candidate'));
            } else if (purgeItem.type === 'job') {
                await jobsApi.purge(purgeItem.id);
                toast.success(t('trash.purge_success_job'));
            } else if (purgeItem.type === 'user') {
                await adminApi.purgeUser(purgeItem.id);
                toast.success(t('trash.purge_success_user'));
            }
            setPurgeItem(null);
            loadTrash();
        } catch (error) {
            toast.error(formatApiError(error));
            setPurgeItem(null);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'analyzed':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#D1FAE5] text-[#059669]">{t('candidates.badge_analyzed')}</span>;
            case 'pending':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#FEF3C7] text-[#D97706]">{t('candidates.badge_pending')}</span>;
            case 'active':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#D1FAE5] text-[#059669]">{t('jobs.badge_active')}</span>;
            default:
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#F3F4F6] text-[#6B7280]">{status}</span>;
        }
    };

    const total = candTotal + jobsTotal + (isAdmin ? usersTotal + orgsTotal : 0);
    const orgConfirmMatches = orgPurgeTarget && orgConfirmName.trim() === (orgPurgeTarget.name || '').trim();

    const Pager = ({ page, totalItems, onPage, testid }) => {
        const pages = Math.max(1, Math.ceil((totalItems || 0) / PAGE_SIZE));
        if ((totalItems || 0) <= PAGE_SIZE) return null;
        return (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E7EB]" data-testid={testid}>
                <span className="text-sm text-[#9CA3AF]">{totalItems} · {t('trash.page_of', { page, pages })}</span>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${testid}-prev`}>{t('trash.prev')}</Button>
                    <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${testid}-next`}>{t('trash.next')}</Button>
                </div>
            </div>
        );
    };

    const subtitle = isAdmin ? t('trash.subtitle_admin')
        : user?.role === 'manager' ? t('trash.subtitle_manager')
        : t('trash.subtitle_user');

    return (
        <div className="space-y-6" data-testid="trash-page">
            {/* Header */}
            <div className="flex items-start gap-3">
                <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="trash-back-btn" className="flex-shrink-0">
                    <ArrowLeft className="w-4 h-4" />
                </Button>
                <div className="min-w-0">
                    <h1 className="text-xl sm:text-2xl font-bold text-[#0A0A0A] font-['Outfit'] flex flex-wrap items-center gap-2">
                        <Archive className="w-6 h-6 text-[#EF4444]" />
                        {t('trash.page_title')}
                        <span className="text-base font-normal text-[#9CA3AF]">({total})</span>
                    </h1>
                    <p className="text-[#4B5563] text-sm">
                        {subtitle} — {t('trash.auto_delete_notice')}
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-48">
                    <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
                </div>
            ) : (
                <Tabs defaultValue="candidates" className="bg-white rounded-lg border border-[#E5E7EB]">
                    <TabsList className="hidden md:flex w-full border-b border-[#E5E7EB] rounded-none bg-transparent p-0">
                        <TabsTrigger
                            value="candidates"
                            className="flex-1 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-[#EF4444] data-[state=active]:text-[#EF4444]"
                            data-testid="trash-tab-candidates"
                        >
                            <Users className="w-4 h-4 mr-2" />
                            {t('trash.tab_candidates')} ({candTotal})
                        </TabsTrigger>
                        <TabsTrigger
                            value="jobs"
                            className="flex-1 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-[#EF4444] data-[state=active]:text-[#EF4444]"
                            data-testid="trash-tab-jobs"
                        >
                            <Briefcase className="w-4 h-4 mr-2" />
                            {t('trash.tab_jobs')} ({jobsTotal})
                        </TabsTrigger>
                        {isAdmin && (
                            <TabsTrigger
                                value="users"
                                className="flex-1 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-[#EF4444] data-[state=active]:text-[#EF4444]"
                                data-testid="trash-tab-users"
                            >
                                <UserCog className="w-4 h-4 mr-2" />
                                {t('trash.tab_users')} ({usersTotal})
                            </TabsTrigger>
                        )}
                        {isAdmin && (
                            <TabsTrigger
                                value="organizations"
                                className="flex-1 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-[#EF4444] data-[state=active]:text-[#EF4444]"
                                data-testid="trash-tab-organizations"
                            >
                                <Building2 className="w-4 h-4 mr-2" />
                                {t('trash.tab_organizations')} ({orgsTotal})
                            </TabsTrigger>
                        )}
                    </TabsList>

                    {/* Mobile-only section header for CANDIDATES */}
                    <div className="md:hidden flex items-center gap-2 px-4 py-3 bg-[#FEF2F2] border-b border-[#E5E7EB]" data-testid="trash-mobile-section-candidates">
                        <Users className="w-4 h-4 text-[#EF4444]" />
                        <h2 className="text-sm font-semibold text-[#0A0A0A]">
                            {t('trash.tab_candidates')} ({candTotal})
                        </h2>
                    </div>

                    {/* Candidates tab */}
                    <TabsContent value="candidates" className="p-0 data-[state=inactive]:md:hidden" forceMount>
                        <div className="px-4 py-3 border-b border-[#E5E7EB] bg-white">
                            <Input value={candInput} onChange={(e) => setCandInput(e.target.value)} placeholder={t('trash.search_candidates')} data-testid="trash-search-candidates" className="max-w-sm h-9" />
                        </div>
                        <Pager page={candPage} totalItems={candTotal} onPage={setCandPage} testid="trash-pager-candidates" />
                        {candidates.length === 0 ? (
                            <div className="p-12 text-center text-[#9CA3AF]">
                                <Archive className="w-16 h-16 mx-auto mb-4 opacity-30" />
                                <h3 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">{t('trash.empty_candidates')}</h3>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                            <table className="w-full min-w-[640px]">
                                <thead className="bg-[#FEF2F2] border-b border-[#E5E7EB]">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase">{t('trash.col_candidate')}</th>
                                        {isAdmin && <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase hidden md:table-cell">{t('trash.col_organization')}</th>}
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase hidden md:table-cell">{t('trash.col_deleted_on')}</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase">{t('trash.col_retention')}</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase hidden sm:table-cell">{t('trash.col_status')}</th>
                                        <th className="px-6 py-3 text-right text-xs font-semibold text-[#4B5563] uppercase">{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#E5E7EB]">
                                    {candidates.map((c) => {
                                        const days = daysUntilPurge(c.deleted_at);
                                        return (
                                            <tr key={c.id} className="hover:bg-[#FEF2F2]/50" data-testid={`trash-row-${c.id}`}>
                                                <td className="px-6 py-4">
                                                    <p className="font-medium text-[#0A0A0A]">{c.name}</p>
                                                    <p className="text-sm text-[#9CA3AF]">{c.email || t('trash.no_email')}</p>
                                                </td>
                                                {isAdmin && (
                                                    <td className="px-6 py-4 hidden md:table-cell text-sm text-[#4B5563]">{c.organization_name || '-'}</td>
                                                )}
                                                <td className="px-6 py-4 hidden md:table-cell text-sm text-[#9CA3AF]">
                                                    {localeDate(c.deleted_at)}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${days <= 3 ? 'bg-[#FEE2E2] text-[#DC2626]' : 'bg-[#FEF3C7] text-[#D97706]'}`}>
                                                        {t('trash.days_remaining', { count: days })}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 hidden sm:table-cell">{getStatusBadge(c.status)}</td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <Button variant="outline" size="sm" onClick={() => handleRestoreCandidate(c.id)} data-testid={`restore-btn-${c.id}`}>
                                                            <RotateCcw className="w-3.5 h-3.5 mr-1" />{t('common.restore')}
                                                        </Button>
                                                        {canPurge && (
                                                            <Button variant="outline" size="sm" className="text-[#EF4444] border-[#EF4444] hover:bg-[#FEF2F2]" onClick={() => setPurgeItem({ type: 'candidate', id: c.id, name: c.name })} data-testid={`purge-btn-${c.id}`}>
                                                                <Trash2 className="w-3.5 h-3.5 mr-1" />{t('trash.purge_button')}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            </div>
                        )}
                    </TabsContent>

                    {/* Jobs tab */}
                    {/* Mobile-only section header for JOBS */}
                    <div className="md:hidden flex items-center gap-2 px-4 py-3 bg-[#FEF2F2] border-t border-b border-[#E5E7EB]" data-testid="trash-mobile-section-jobs">
                        <Briefcase className="w-4 h-4 text-[#EF4444]" />
                        <h2 className="text-sm font-semibold text-[#0A0A0A]">
                            {t('trash.tab_jobs')} ({jobsTotal})
                        </h2>
                    </div>

                    <TabsContent value="jobs" className="p-0 data-[state=inactive]:md:hidden" forceMount>
                        <div className="px-4 py-3 border-b border-[#E5E7EB] bg-white">
                            <Input value={jobsInput} onChange={(e) => setJobsInput(e.target.value)} placeholder={t('trash.search_jobs')} data-testid="trash-search-jobs" className="max-w-sm h-9" />
                        </div>
                        <Pager page={jobsPage} totalItems={jobsTotal} onPage={setJobsPage} testid="trash-pager-jobs" />
                        {jobs.length === 0 ? (
                            <div className="p-12 text-center text-[#9CA3AF]">
                                <Briefcase className="w-16 h-16 mx-auto mb-4 opacity-30" />
                                <h3 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">{t('trash.empty_jobs')}</h3>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                            <table className="w-full min-w-[640px]">
                                <thead className="bg-[#FEF2F2] border-b border-[#E5E7EB]">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase">{t('trash.col_job')}</th>
                                        {isAdmin && <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase hidden md:table-cell">{t('trash.col_organization')}</th>}
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase hidden md:table-cell">{t('trash.col_linked_candidates')}</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase hidden md:table-cell">{t('trash.col_deleted_on')}</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase">{t('trash.col_retention')}</th>
                                        <th className="px-6 py-3 text-right text-xs font-semibold text-[#4B5563] uppercase">{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#E5E7EB]">
                                    {jobs.map((j) => {
                                        const days = daysUntilPurge(j.deleted_at);
                                        return (
                                            <tr key={j.id} className="hover:bg-[#FEF2F2]/50" data-testid={`trash-job-row-${j.id}`}>
                                                <td className="px-6 py-4">
                                                    <p className="font-medium text-[#0A0A0A]">{j.title}</p>
                                                    <p className="text-sm text-[#9CA3AF]">{j.department || '-'} {j.location ? ` / ${j.location}` : ''}</p>
                                                </td>
                                                {isAdmin && (
                                                    <td className="px-6 py-4 hidden md:table-cell text-sm text-[#4B5563]">{j.organization_name || '-'}</td>
                                                )}
                                                <td className="px-6 py-4 hidden md:table-cell text-sm text-[#4B5563]">
                                                    {j.linked_candidates_count || 0}
                                                </td>
                                                <td className="px-6 py-4 hidden md:table-cell text-sm text-[#9CA3AF]">
                                                    {localeDate(j.deleted_at)}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${days <= 3 ? 'bg-[#FEE2E2] text-[#DC2626]' : 'bg-[#FEF3C7] text-[#D97706]'}`}>
                                                        {t('trash.days_remaining', { count: days })}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <Button variant="outline" size="sm" onClick={() => handleRestoreJob(j.id)} data-testid={`restore-job-btn-${j.id}`}>
                                                            <RotateCcw className="w-3.5 h-3.5 mr-1" />{t('common.restore')}
                                                        </Button>
                                                        {canPurge && (
                                                            <Button variant="outline" size="sm" className="text-[#EF4444] border-[#EF4444] hover:bg-[#FEF2F2]" onClick={() => setPurgeItem({ type: 'job', id: j.id, name: j.title })} data-testid={`purge-job-btn-${j.id}`}>
                                                                <Trash2 className="w-3.5 h-3.5 mr-1" />{t('trash.purge_button')}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            </div>
                        )}
                    </TabsContent>

                    {/* Users tab (admin only) */}
                    {isAdmin && (
                        <>
                        {/* Mobile-only section header for USERS */}
                        <div className="md:hidden flex items-center gap-2 px-4 py-3 bg-[#FEF2F2] border-t border-b border-[#E5E7EB]" data-testid="trash-mobile-section-users">
                            <UserCog className="w-4 h-4 text-[#EF4444]" />
                            <h2 className="text-sm font-semibold text-[#0A0A0A]">
                                {t('trash.tab_users')} ({usersTotal})
                            </h2>
                        </div>

                        <TabsContent value="users" className="p-0 data-[state=inactive]:md:hidden" forceMount>
                            <div className="px-4 py-3 border-b border-[#E5E7EB] bg-white">
                                <Input value={usersInput} onChange={(e) => setUsersInput(e.target.value)} placeholder={t('trash.search_users')} data-testid="trash-search-users" className="max-w-sm h-9" />
                            </div>
                            <Pager page={usersPage} totalItems={usersTotal} onPage={setUsersPage} testid="trash-pager-users" />
                            {users.length === 0 ? (
                                <div className="p-12 text-center text-[#9CA3AF]">
                                    <UserCog className="w-16 h-16 mx-auto mb-4 opacity-30" />
                                    <h3 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">{t('trash.empty_users')}</h3>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                <table className="w-full min-w-[640px]">
                                    <thead className="bg-[#FEF2F2] border-b border-[#E5E7EB]">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase">{t('trash.col_user')}</th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase hidden md:table-cell">{t('trash.col_company')}</th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase hidden md:table-cell">{t('trash.col_role')}</th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase hidden md:table-cell">{t('trash.col_deleted_on')}</th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase">{t('trash.col_retention')}</th>
                                            <th className="px-6 py-3 text-right text-xs font-semibold text-[#4B5563] uppercase">{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#E5E7EB]">
                                        {users.map((u) => {
                                            const days = daysUntilPurge(u.deleted_at);
                                            return (
                                                <tr key={u.id} className="hover:bg-[#FEF2F2]/50" data-testid={`trash-user-row-${u.id}`}>
                                                    <td className="px-6 py-4">
                                                        <p className="font-medium text-[#0A0A0A]">{u.name}</p>
                                                        <p className="text-sm text-[#9CA3AF]">{u.email}</p>
                                                    </td>
                                                    <td className="px-6 py-4 hidden md:table-cell text-sm text-[#4B5563]">{u.organization_name || '-'}</td>
                                                    <td className="px-6 py-4 hidden md:table-cell text-sm text-[#4B5563] capitalize">{u.role}</td>
                                                    <td className="px-6 py-4 hidden md:table-cell text-sm text-[#9CA3AF]">
                                                        {localeDate(u.deleted_at)}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${days <= 3 ? 'bg-[#FEE2E2] text-[#DC2626]' : 'bg-[#FEF3C7] text-[#D97706]'}`}>
                                                            {t('trash.days_remaining', { count: days })}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <Button variant="outline" size="sm" onClick={() => handleRestoreUser(u.id)} data-testid={`restore-user-btn-${u.id}`}>
                                                                <RotateCcw className="w-3.5 h-3.5 mr-1" />{t('common.restore')}
                                                            </Button>
                                                            <Button variant="outline" size="sm" className="text-[#EF4444] border-[#EF4444] hover:bg-[#FEF2F2]" onClick={() => setPurgeItem({ type: 'user', id: u.id, name: u.name })} data-testid={`purge-user-btn-${u.id}`}>
                                                                <Trash2 className="w-3.5 h-3.5 mr-1" />{t('trash.purge_button')}
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                </div>
                            )}
                        </TabsContent>
                        </>
                    )}

                    {/* Organizations tab (super-admin only) */}
                    {isAdmin && (
                        <>
                        {/* Mobile-only section header for ORGANIZATIONS */}
                        <div className="md:hidden flex items-center gap-2 px-4 py-3 bg-[#FEF2F2] border-t border-b border-[#E5E7EB]" data-testid="trash-mobile-section-organizations">
                            <Building2 className="w-4 h-4 text-[#EF4444]" />
                            <h2 className="text-sm font-semibold text-[#0A0A0A]">
                                {t('trash.tab_organizations')} ({orgsTotal})
                            </h2>
                        </div>

                        <TabsContent value="organizations" className="p-0 data-[state=inactive]:md:hidden" forceMount>
                            <div className="px-4 py-3 border-b border-[#E5E7EB] bg-white">
                                <Input value={orgsInput} onChange={(e) => setOrgsInput(e.target.value)} placeholder={t('trash.search_organizations')} data-testid="trash-search-organizations" className="max-w-sm h-9" />
                            </div>
                            <Pager page={orgsPage} totalItems={orgsTotal} onPage={setOrgsPage} testid="trash-pager-organizations" />
                            {orgs.length === 0 ? (
                                <div className="p-12 text-center text-[#9CA3AF]">
                                    <Building2 className="w-16 h-16 mx-auto mb-4 opacity-30" />
                                    <h3 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">{t('trash.empty_organizations')}</h3>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                <table className="w-full min-w-[640px]">
                                    <thead className="bg-[#FEF2F2] border-b border-[#E5E7EB]">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase">{t('trash.col_organization')}</th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase hidden md:table-cell">{t('trash.col_deleted_on')}</th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase hidden md:table-cell">{t('trash.col_members')}</th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase hidden md:table-cell">{t('trash.tab_jobs')}</th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase hidden md:table-cell">{t('trash.tab_candidates')}</th>
                                            <th className="px-6 py-3 text-right text-xs font-semibold text-[#4B5563] uppercase">{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#E5E7EB]">
                                        {orgs.map((org) => (
                                            <tr key={org.id} className="hover:bg-[#FEF2F2]/50" data-testid={`trash-org-row-${org.id}`}>
                                                <td className="px-6 py-4">
                                                    <p className="font-medium text-[#0A0A0A]">{org.name}</p>
                                                    <p className="text-sm text-[#9CA3AF] capitalize">{org.subscription_plan || '-'}</p>
                                                </td>
                                                <td className="px-6 py-4 hidden md:table-cell text-sm text-[#9CA3AF]">
                                                    {localeDate(org.deleted_at)}
                                                </td>
                                                <td className="px-6 py-4 hidden md:table-cell text-sm text-[#4B5563]">{org.members_count ?? 0}</td>
                                                <td className="px-6 py-4 hidden md:table-cell text-sm text-[#4B5563]">{org.jobs_total ?? 0}</td>
                                                <td className="px-6 py-4 hidden md:table-cell text-sm text-[#4B5563]">{org.candidates_total ?? 0}</td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <Button variant="outline" size="sm" onClick={() => handleRestoreOrg(org.id)} data-testid={`restore-org-btn-${org.id}`}>
                                                            <RotateCcw className="w-3.5 h-3.5 mr-1" />{t('common.restore')}
                                                        </Button>
                                                        <Button variant="outline" size="sm" className="text-[#EF4444] border-[#EF4444] hover:bg-[#FEF2F2]" onClick={() => { setOrgPurgeTarget(org); setOrgConfirmName(''); }} data-testid={`purge-org-btn-${org.id}`}>
                                                            <Trash2 className="w-3.5 h-3.5 mr-1" />{t('trash.purge_button')}
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                </div>
                            )}
                        </TabsContent>
                        </>
                    )}
                </Tabs>
            )}

            {/* Purge confirmation */}
            <AlertDialog open={!!purgeItem} onOpenChange={() => setPurgeItem(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('trash.purge_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {purgeItem?.type === 'job' ? t('trash.purge_desc_job', { name: purgeItem?.name })
                                : purgeItem?.type === 'user' ? t('trash.purge_desc_user', { name: purgeItem?.name })
                                : t('trash.purge_desc_candidate', { name: purgeItem?.name })}
                            <br /><br />{t('trash.purge_irreversible')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={handlePurge} className="bg-[#EF4444] hover:bg-[#DC2626] text-white" data-testid="confirm-purge-btn">
                            {t('trash.purge_confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Organization permanent delete confirmation (name confirmation required) */}
            <AlertDialog open={!!orgPurgeTarget} onOpenChange={(o) => { if (!o) { setOrgPurgeTarget(null); setOrgConfirmName(''); } }}>
                <AlertDialogContent data-testid="purge-org-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-[#DC2626]">
                            <AlertTriangle className="w-5 h-5" />
                            {t('trash.purge_org_title')}
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm">
                                <p className="text-[#4B5563]">
                                    <strong className="text-[#DC2626]">{t('trash.purge_irreversible')}</strong>{' '}
                                    {t('trash.purge_org_desc')}
                                </p>
                                <p className="text-[#4B5563]">{t('trash.purge_org_confirm_label')}</p>
                                <p className="font-mono bg-[#F3F4F6] px-3 py-2 rounded text-[#0A0A0A] text-xs">
                                    {orgPurgeTarget?.name}
                                </p>
                                <Input
                                    value={orgConfirmName}
                                    onChange={(e) => setOrgConfirmName(e.target.value)}
                                    placeholder={t('trash.purge_org_name_placeholder')}
                                    data-testid="purge-org-confirm-input"
                                    autoFocus
                                />
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={orgWorking}>{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={handlePurgeOrg} disabled={orgWorking || !orgConfirmMatches} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="confirm-purge-org-btn">
                            {orgWorking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('trash.purge_confirm')}…</> : t('trash.purge_confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
